import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Package, Plus, ArrowRightLeft, Loader2, PackageOpen, Trash2, Pencil, AlertTriangle, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

export default function Inventory() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "manager" || user?.role === "op_manager";
  const canDelete = user?.role === "admin";

  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);

  const [itemDialog, setItemDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({ name: "", category: "", category_id: "", unit: "pcs", base_unit: "", sku: "", reorder_level: 0 });
  const [savingItem, setSavingItem] = useState(false);

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferItemId, setTransferItemId] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [transferQty, setTransferQty] = useState("");
  const [transferNote, setTransferNote] = useState("");
  const [transferring, setTransferring] = useState(false);

  const loadOutlets = useCallback(async () => {
    const { data } = await api.get("/outlets");
    const nonInternal = data.filter((o) => !o.is_internal);
    setOutlets(nonInternal);
    // Auto-pick first outlet on initial load
    setOutletId((prev) => prev || (nonInternal[0]?.id ?? ""));
  }, []);

  const loadItems = useCallback(async (oid) => {
    const q = oid ? `?outlet_id=${oid}` : "";
    const { data } = await api.get(`/inventory/items${q}`);
    setItems(data);
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch { /* silently ignore for non-admin roles */ }
  }, []);

  useEffect(() => { loadOutlets(); loadCategories(); }, [loadOutlets, loadCategories]);
  useEffect(() => { if (outletId) loadItems(outletId); }, [outletId, loadItems]);

  const filtered = useMemo(() => {
    if (!items) return null;
    const s = q.trim().toLowerCase();
    // Toggle OFF (default) → only rows with stock on hand at the selected outlet.
    // Toggle ON → every catalog item, even zero-stock ones.
    const base = showAll ? items : items.filter((x) => (x.on_hand ?? 0) > 0);
    if (!s) return base;
    return base.filter((x) =>
      x.name.toLowerCase().includes(s) ||
      (x.category || "").toLowerCase().includes(s) ||
      (x.sku || "").toLowerCase().includes(s)
    );
  }, [items, q, showAll]);

  const selectedOutletName = outlets.find((o) => o.id === outletId)?.name || "outlet";

  const openNewItem = () => {
    setForm({ name: "", category: "", category_id: "", unit: "pcs", base_unit: "", sku: "", reorder_level: 0 });
    setItemDialog({ open: true, editing: null });
  };

  const openEditItem = (it) => {
    setForm({
      name: it.name || "",
      category: it.category || "",
      category_id: it.category_id || "",
      unit: it.unit || "pcs",
      base_unit: it.base_unit || "",
      sku: it.sku || "",
      reorder_level: it.reorder_level || 0,
    });
    setItemDialog({ open: true, editing: it });
  };

  const saveItem = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name required");
    if (!form.base_unit.trim()) return toast.error("Base unit is required (e.g. grams, pieces, ml)");
    setSavingItem(true);
    try {
      const payload = {
        ...form,
        base_unit: form.base_unit.trim().toLowerCase(),
        reorder_level: parseInt(form.reorder_level, 10) || 0,
        category_id: form.category_id || null,
      };
      if (itemDialog.editing) {
        await api.put(`/inventory/items/${itemDialog.editing.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/inventory/items", payload);
        toast.success("Item added");
      }
      setItemDialog({ open: false, editing: null });
      await loadItems(outletId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingItem(false);
    }
  };

  const deleteItem = async (it) => {
    if (!confirm(`Delete "${it.name}" permanently? Stock levels will remain in history.`)) return;
    try {
      await api.delete(`/inventory/items/${it.id}`);
      toast.success("Item deleted");
      loadItems(outletId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const openTransfer = (it) => {
    setTransferItemId(it?.id || "");
    setTransferTo("");
    setTransferQty("");
    setTransferNote("");
    setTransferOpen(true);
  };

  // Live-derived from `items` so the dropdown always reflects the freshest on_hand
  const transferItem = useMemo(
    () => (items || []).find((x) => x.id === transferItemId) || null,
    [items, transferItemId]
  );
  const transferMax = transferItem?.on_hand ?? 0;
  const qtyNum = parseInt(transferQty, 10);
  const overMax = Number.isFinite(qtyNum) && transferItem && qtyNum > transferMax;

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!transferItem) return toast.error("Pick an item to transfer");
    const qty = parseInt(transferQty, 10);
    if (!qty || qty <= 0) return toast.error("Enter a positive quantity");
    if (qty > transferMax) return toast.error("Insufficient stock at source");
    if (!transferTo) return toast.error("Pick a destination outlet");
    setTransferring(true);
    try {
      await api.post("/inventory/transfers", {
        item_id: transferItem.id,
        from_outlet_id: outletId,
        to_outlet_id: transferTo,
        qty,
        note: transferNote,
      });
      toast.success(`Transferred ${qty} ${transferItem.unit || ""} of ${transferItem.name}`);
      setTransferOpen(false);
      // Auto-refresh the table after a successful transfer
      await loadItems(outletId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setTransferring(false);
    }
  };

  const receiveStock = async (it) => {
    const raw = prompt(`Receive stock for "${it.name}" at ${selectedOutletName}\nEnter quantity (positive to add, negative to consume):`, "1");
    if (raw === null) return;
    const qty = parseInt(raw, 10);
    if (!qty) return toast.error("Enter a non-zero quantity");
    try {
      await api.post("/inventory/adjust", { item_id: it.id, outlet_id: outletId, qty, note: "" });
      toast.success(qty > 0 ? `Added ${qty} to ${selectedOutletName}` : `Consumed ${-qty} from ${selectedOutletName}`);
      loadItems(outletId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-900">Inventory</h1>
          <p className="text-sm text-slate-500 mt-1">
            Consumables & spare parts. Track total stock across all outlets and move it between them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <Button
              data-testid="inv-transfer-open"
              onClick={() => openTransfer(null)}
              disabled={outlets.length < 2 || !(items || []).some((x) => (x.on_hand ?? 0) > 0)}
              variant="outline"
              className="h-11"
              title={outlets.length < 2 ? "Need at least two outlets to transfer" : "Move stock between outlets"}
            >
              <ArrowRightLeft className="w-4 h-4 mr-1.5" /> Transfer stock
            </Button>
          )}
          {canWrite && (
            <Button data-testid="inv-new-item" onClick={openNewItem} className="h-11 bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Add item
            </Button>
          )}
        </div>
      </div>

      {/* Outlet + search bar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-slate-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 shrink-0">Outlet</span>
          <Select value={outletId} onValueChange={setOutletId}>
            <SelectTrigger data-testid="inv-outlet-select" className="h-10 w-64">
              <SelectValue placeholder="Pick outlet" />
            </SelectTrigger>
            <SelectContent>
              {outlets.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 sm:max-w-sm">
          <Input
            data-testid="inv-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search item, category, SKU…"
            className="h-10"
          />
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <Switch
            data-testid="inv-show-all"
            id="inv-show-all"
            checked={showAll}
            onCheckedChange={setShowAll}
          />
          <Label htmlFor="inv-show-all" className="text-xs font-semibold text-slate-600 cursor-pointer">
            Show all items
            <span className="ml-1 font-normal text-slate-400">(incl. zero stock)</span>
          </Label>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Item</th>
                <th className="text-left px-4 py-3 font-semibold">Category</th>
                <th className="text-right px-4 py-3 font-semibold" data-testid="col-total-all-outlets">
                  Total (All Outlets)
                </th>
                <th className="text-right px-4 py-3 font-semibold" data-testid="col-on-hand">
                  On Hand ({outlets.find((o) => o.id === outletId)?.code || "—"})
                </th>
                <th className="text-right px-4 py-3 font-semibold" data-testid="col-unit-cost">
                  Unit Cost (₹)
                </th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered === null && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                  <Loader2 className="w-4 h-4 mx-auto animate-spin" />
                </td></tr>
              )}
              {filtered && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                  <PackageOpen className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                  <div className="font-semibold text-slate-700">
                    {showAll ? "No items yet" : "No stock at this outlet"}
                  </div>
                  <div className="text-xs">
                    {showAll
                      ? "Add your first spare or consumable to start tracking stock."
                      : "Turn on \"Show all items\" to see every catalog entry, or receive stock to stock this outlet."}
                  </div>
                </td></tr>
              )}
              {filtered && filtered.map((it) => {
                const low = it.reorder_level > 0 && (it.on_hand ?? 0) < it.reorder_level;
                return (
                  <tr key={it.id} data-testid={`inv-row-${it.id}`} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-lg bg-indigo-50 grid place-items-center text-indigo-600">
                          <Package className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{it.name}</div>
                          {it.sku && <div className="text-[11px] text-slate-500">SKU · {it.sku}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{it.category || "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-800" data-testid={`inv-total-${it.id}`}>
                      {it.total} <span className="text-[11px] font-normal text-slate-500">{it.unit || ""}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold" data-testid={`inv-on-hand-${it.id}`}>
                      <span className={low ? "text-rose-600" : "text-slate-800"}>{it.on_hand ?? 0}</span>
                      <span className="text-[11px] font-normal text-slate-500 ml-1">{it.base_unit || it.unit || ""}</span>
                      {low && (
                        <div className="text-[10px] text-rose-600 font-medium mt-0.5 inline-flex items-center gap-0.5 justify-end w-full">
                          <AlertTriangle className="w-3 h-3" /> Below reorder ({it.reorder_level})
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700" data-testid={`inv-unit-cost-${it.id}`}>
                      {it.unit_cost ? `₹${Number(it.unit_cost).toFixed(4)}` : <span className="text-slate-400">—</span>}
                      {it.base_unit && <div className="text-[10px] text-slate-500">per {it.base_unit}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canWrite && (
                          <button
                            data-testid={`inv-receive-${it.id}`}
                            onClick={() => receiveStock(it)}
                            className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100"
                            title="Receive / adjust stock"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                        {canWrite && (
                          <button
                            data-testid={`inv-transfer-${it.id}`}
                            onClick={() => openTransfer(it)}
                            disabled={(it.on_hand ?? 0) <= 0 || outlets.length < 2}
                            className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={(it.on_hand ?? 0) <= 0 ? "No stock to transfer from this outlet" : "Transfer to another outlet"}
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                          </button>
                        )}
                        {canWrite && (
                          <button
                            data-testid={`inv-edit-${it.id}`}
                            onClick={() => openEditItem(it)}
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
                            title="Edit item"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            data-testid={`inv-delete-${it.id}`}
                            onClick={() => deleteItem(it)}
                            className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50"
                            title="Delete item"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Item create / edit dialog */}
      <Dialog open={itemDialog.open} onOpenChange={(v) => setItemDialog((s) => ({ ...s, open: v }))}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{itemDialog.editing ? "Edit item" : "Add inventory item"}</DialogTitle></DialogHeader>
          <form onSubmit={saveItem} className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="inv-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Grease trap filter" className="mt-1.5 h-11" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Input data-testid="inv-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="HVAC / Plumbing…" className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>Display Unit</Label>
                <Input data-testid="inv-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="pcs / m / kg" className="mt-1.5 h-11" />
              </div>
            </div>
            <div>
              <Label>Category (drives zone membership)</Label>
              <Select value={form.category_id || "_"} onValueChange={(v) => setForm({ ...form, category_id: v === "_" ? "" : v })}>
                <SelectTrigger data-testid="inv-form-category-id" className="mt-1.5 h-11">
                  <SelectValue placeholder="Uncategorised" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_">Uncategorised</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">
                Zones are derived from the category — items follow the category wherever it goes.
              </p>
            </div>
            <div>
              <Label>Base Unit <span className="text-rose-500">*</span></Label>
              <Input
                data-testid="inv-form-base-unit"
                required
                value={form.base_unit}
                onChange={(e) => setForm({ ...form, base_unit: e.target.value })}
                placeholder="grams · pieces · ml"
                className="mt-1.5 h-11"
                disabled={!!itemDialog.editing}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                All stock and cost math is stored in this unit. {itemDialog.editing ? "Locked once set." : "Choose carefully — this can't be changed after items are added."}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>SKU (optional)</Label>
                <Input data-testid="inv-form-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label>Reorder at</Label>
                <Input data-testid="inv-form-reorder" type="number" min="0" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} className="mt-1.5 h-11" />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={savingItem} data-testid="inv-form-submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {savingItem ? <Loader2 className="w-4 h-4 animate-spin" /> : (itemDialog.editing ? "Save changes" : "Add item")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Transfer stock</DialogTitle></DialogHeader>
          <form onSubmit={submitTransfer} className="space-y-3">
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600">
              Source: <span className="font-semibold text-slate-900">{selectedOutletName}</span>
            </div>
            <div>
              <Label>Item</Label>
              <Select value={transferItemId} onValueChange={(v) => { setTransferItemId(v); setTransferQty(""); }}>
                <SelectTrigger data-testid="inv-transfer-item" className="mt-1.5 h-11">
                  <SelectValue placeholder="Pick an item at source" />
                </SelectTrigger>
                <SelectContent>
                  {(items || []).filter((x) => (x.on_hand ?? 0) > 0).length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-slate-500">
                      No stock at {selectedOutletName} yet. Receive some first.
                    </div>
                  ) : (
                    (items || [])
                      .filter((x) => (x.on_hand ?? 0) > 0)
                      .map((it) => (
                        <SelectItem key={it.id} value={it.id} data-testid={`inv-transfer-item-opt-${it.id}`}>
                          {it.name} <span className="text-slate-500">({it.on_hand} {it.unit || "units"})</span>
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
              {transferItem && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Available at {selectedOutletName}: <span className="font-semibold tabular-nums text-slate-800">{transferMax}</span> {transferItem.unit || ""}
                </p>
              )}
            </div>
            <div>
              <Label>To outlet</Label>
              <Select value={transferTo} onValueChange={setTransferTo}>
                <SelectTrigger data-testid="inv-transfer-to" className="mt-1.5 h-11"><SelectValue placeholder="Pick destination" /></SelectTrigger>
                <SelectContent>
                  {outlets.filter((o) => o.id !== outletId).map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                data-testid="inv-transfer-qty"
                type="number"
                min="1"
                max={transferMax || undefined}
                required
                value={transferQty}
                onChange={(e) => setTransferQty(e.target.value)}
                className={`mt-1.5 h-11 ${overMax ? "border-rose-400 focus-visible:ring-rose-400" : ""}`}
                placeholder={transferItem ? `up to ${transferMax}` : "Pick an item first"}
                disabled={!transferItem}
                aria-invalid={overMax || undefined}
              />
              {overMax && (
                <p data-testid="inv-transfer-error" className="mt-1 text-[11px] font-semibold text-rose-600">
                  Insufficient stock at source
                </p>
              )}
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea data-testid="inv-transfer-note" value={transferNote} onChange={(e) => setTransferNote(e.target.value)} className="mt-1.5" placeholder="e.g. Restock Koramangala" />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={transferring || !transferItem || !transferQty || overMax || !transferTo}
                data-testid="inv-transfer-submit"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {transferring ? <Loader2 className="w-4 h-4 animate-spin" /> : "Transfer stock"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
